import { getDatabase, ref, onDisconnect, set, serverTimestamp, onValue, off } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { initializeApp, getApps, getApp } from 'firebase/app';
import { UserOnlineStatus } from '../enums/auth';

// Firebase 설정
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

// Firebase 앱 초기화 (중복 방지)
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getDatabase(app);

class RealtimeService {
  constructor() {
    this.db = db;
    this.auth = auth;
    this.statusListeners = new Map();
    this.setupAuthStateListener();
  }

  // 인증 상태 변경 리스너 설정
  setupAuthStateListener() {
    let previousUser = null;
    
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        // 로그인 시
        this.setUserOnline(user.uid);
        previousUser = user;
      } else {
        // 로그아웃 시 - 이전 사용자 정보를 사용하여 오프라인 상태로 설정
        if (previousUser) {
          this.setUserOffline(previousUser.uid);
          previousUser = null;
        }
      }
    });
  }

  // 사용자를 온라인 상태로 설정
  setUserOnline(userId) {
    const userStatusRef = ref(this.db, "/status/" + userId);
    
    // 접속 중으로 표시
    set(userStatusRef, {
      state: UserOnlineStatus.ONLINE,
      last_changed: serverTimestamp(),
      last_seen: serverTimestamp()
    });

    // 브라우저 종료 등으로 연결 끊길 경우 자동 업데이트
    onDisconnect(userStatusRef).set({
      state: UserOnlineStatus.OFFLINE,
      last_changed: serverTimestamp(),
      last_seen: serverTimestamp()
    });
  }

  // 사용자를 오프라인 상태로 설정
  setUserOffline(userId) {
    const userStatusRef = ref(this.db, "/status/" + userId);
    set(userStatusRef, {
      state: UserOnlineStatus.OFFLINE,
      last_changed: serverTimestamp(),
      last_seen: serverTimestamp()
    });
  }

  // 모든 사용자의 실시간 상태 감지
  listenToUserStatus(callback) {
    const statusRef = ref(this.db, "/status");
    
    const listener = onValue(statusRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const onlineUsers = Object.keys(data).filter(uid => 
          data[uid] && data[uid].state === UserOnlineStatus.ONLINE
        );
        callback(onlineUsers, data);
      } else {
        callback([], {});
      }
    });

    // 리스너 저장 (나중에 정리하기 위해)
    this.statusListeners.set('all', listener);
    
    return () => {
      off(statusRef);
      this.statusListeners.delete('all');
    };
  }

  // 특정 사용자의 상태 감지
  listenToSpecificUserStatus(userId, callback) {
    const userStatusRef = ref(this.db, "/status/" + userId);
    
    const listener = onValue(userStatusRef, (snapshot) => {
      const data = snapshot.val();
      callback(data);
    });

    this.statusListeners.set(userId, listener);
    
    return () => {
      off(userStatusRef);
      this.statusListeners.delete(userId);
    };
  }

  // 모든 리스너 정리
  cleanup() {
    this.statusListeners.forEach((listener, key) => {
      if (typeof listener === 'function') {
        listener();
      }
    });
    this.statusListeners.clear();
  }

  // 현재 온라인 사용자 수 가져오기
  async getOnlineUserCount() {
    return new Promise((resolve) => {
      const statusRef = ref(this.db, "/status");
      
      const listener = onValue(statusRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const onlineCount = Object.keys(data).filter(uid => 
            data[uid] && data[uid].state === UserOnlineStatus.ONLINE
          ).length;
          resolve(onlineCount);
        } else {
          resolve(0);
        }
        
        // 한 번만 실행되도록 리스너 제거
        off(statusRef);
      });
    });
  }

  // 사용자 상태 정보 가져오기
  async getUserStatus(userId) {
    return new Promise((resolve) => {
      const userStatusRef = ref(this.db, "/status/" + userId);
      
      const listener = onValue(userStatusRef, (snapshot) => {
        const data = snapshot.val();
        resolve(data);
        
        // 한 번만 실행되도록 리스너 제거
        off(userStatusRef);
      });
    });
  }
}

// 싱글톤 인스턴스 생성
const realtimeService = new RealtimeService();

export default realtimeService; 